import React from 'react';
import { DataTable, ProgressBar, StatusBadge } from './ui';
import {
  assignmentGrade,
  bestExerciseScore,
  bestQuizScore,
  byId,
  derivedAssignmentStatus,
  fullName,
  getAssignmentProgress,
  getAssignmentsForProgramAssignment,
  getExercisesForVersion,
  getProgramCourses,
  getProgramProgress,
  getQuizzesForVersion,
  programGrade
} from '../utils/lmsSelectors';

function scoreLabel(value) {
  return value === null || value === undefined ? '—' : `${value}%`;
}

export function CourseGradingSheet({ data, courseId, onlyTrainerId = '' }) {
  const course = byId(data.courses, courseId);
  if (!course) return <p className="muted">Select a course to view grades.</p>;
  const quizzes = getQuizzesForVersion(data, course.currentVersionId);
  const exercises = getExercisesForVersion(data, course.currentVersionId);
  const assignments = data.assignments
    .filter((assignment) => assignment.courseId === courseId && assignment.status !== 'revoked')
    .filter(() => !onlyTrainerId || course.ownerId === onlyTrainerId || (course.trainerIds || []).includes(onlyTrainerId));

  const rows = assignments.map((assignment) => {
    const learner = byId(data.users, assignment.assignedToUserId);
    const progress = getAssignmentProgress(data, assignment);
    const row = {
      id: assignment.id,
      learner: fullName(learner),
      email: learner?.email || '',
      status: derivedAssignmentStatus(data, assignment),
      progress: progress.percent,
      overall: assignmentGrade(data, assignment),
      dueDate: assignment.dueDate || '—'
    };
    quizzes.forEach((quiz) => {
      const attempts = assignment.quizAttempts?.filter((attempt) => attempt.quizId === quiz.id) || [];
      row[`quiz-${quiz.id}`] = { score: bestQuizScore(assignment, quiz.id), attempts: attempts.length };
    });
    exercises.forEach((exercise) => {
      const submissions = assignment.exerciseSubmissions?.filter((submission) => submission.exerciseId === exercise.id) || [];
      row[`exercise-${exercise.id}`] = { score: bestExerciseScore(assignment, exercise.id), attempts: submissions.length, status: submissions.at(-1)?.status || 'not_started' };
    });
    return row;
  });

  const columns = [
    { key: 'learner', label: 'Learner', render: (row) => <div><strong>{row.learner}</strong><p className="muted small">{row.email}</p></div> },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'progress', label: 'Progress', render: (row) => <ProgressBar value={row.progress} /> },
    ...quizzes.map((quiz) => ({
      key: `quiz-${quiz.id}`,
      label: quiz.title,
      render: (row) => <div><strong>{scoreLabel(row[`quiz-${quiz.id}`]?.score)}</strong><p className="muted small">Quiz • {row[`quiz-${quiz.id}`]?.attempts || 0} attempts</p></div>
    })),
    ...exercises.map((exercise) => ({
      key: `exercise-${exercise.id}`,
      label: exercise.title,
      render: (row) => <div><strong>{scoreLabel(row[`exercise-${exercise.id}`]?.score)}</strong><p className="muted small">Exercise • {row[`exercise-${exercise.id}`]?.attempts || 0} submissions</p></div>
    })),
    { key: 'overall', label: 'Overall grade', render: (row) => scoreLabel(row.overall) },
    { key: 'dueDate', label: 'Due date' }
  ];

  return <DataTable rows={rows} columns={columns} emptyMessage="No learners are assigned to this course yet." />;
}

export function ProgramGradingSheet({ data, programId }) {
  const program = byId(data.programs || [], programId);
  if (!program) return <p className="muted">Select a program to view grades.</p>;
  const programCourses = getProgramCourses(data, programId);
  const rows = (data.programAssignments || [])
    .filter((assignment) => assignment.programId === programId && assignment.status !== 'revoked')
    .map((programAssignment) => {
      const learner = byId(data.users, programAssignment.assignedToUserId);
      const courseAssignments = getAssignmentsForProgramAssignment(data, programAssignment);
      const progress = getProgramProgress(data, programAssignment);
      const row = {
        id: programAssignment.id,
        learner: fullName(learner),
        email: learner?.email || '',
        status: progress.status,
        progress: progress.percent,
        overall: programGrade(data, programAssignment),
        dueDate: programAssignment.dueDate || '—'
      };
      programCourses.forEach(({ course }) => {
        const courseAssignment = courseAssignments.find((assignment) => assignment.courseId === course.id);
        row[`course-${course.id}`] = courseAssignment ? assignmentGrade(data, courseAssignment) : null;
      });
      return row;
    });

  const columns = [
    { key: 'learner', label: 'Learner', render: (row) => <div><strong>{row.learner}</strong><p className="muted small">{row.email}</p></div> },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'progress', label: 'Program progress', render: (row) => <ProgressBar value={row.progress} /> },
    ...programCourses.map(({ course }) => ({
      key: `course-${course.id}`,
      label: course.title,
      render: (row) => scoreLabel(row[`course-${course.id}`])
    })),
    { key: 'overall', label: 'Program grade', render: (row) => scoreLabel(row.overall) },
    { key: 'dueDate', label: 'Due date' }
  ];

  return <DataTable rows={rows} columns={columns} emptyMessage="No learners are assigned to this program yet." />;
}
